import React, { useState } from 'react';
import { useTranslation } from 'next-i18next';
import { useRouter } from 'next/router';
import Image from 'next/image';

interface NewFooterMobileProps {
  t: any;
  locale: string;
}

interface AccordionState {
  [key: string]: boolean;
}

export const NewFooterMobile: React.FC<NewFooterMobileProps> = ({ t, locale }) => {
  const [accordionState, setAccordionState] = useState<AccordionState>({});

  const toggleAccordion = (key: string) => {
    setAccordionState(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  // Category data for accordion
  const categoryData = [
    { category: t('condo'), key: 'condo' },
    { category: t('house'), key: 'house' },
    { category: t('townhouse'), key: 'townhouse' },
    { category: t('land'), key: 'land' },
    { category: t('apartment'), key: 'apartment' }
  ];

  // Generate links function
  const generateLinks = (categoryData) => {
    const spaceText = locale === "en" ? " " : "";
    const formatText = (text1, text2, options?) => {
      if(locale === "cn") {
        const textfilter = ['New Project', 'Second Hand', 'Cheap Price', 'Lumpini', 'Sukhumvit', 'Ratchada', 'Lat Phrao', 'Pattaya', 'Chiang Mai', 'Hua Hin'];
        if(textfilter.includes(text2)){
          return `${t(text2)}${t(text1)}`
        }
        return `${t(text1)}${t(text2)}`
      } else {
        if(options) return `${t(text1)}${t(options)}${t(text2)}`
        return `${t(text1)}${t(text2)}`
      }
    };

    let list = [
      { text: formatText(t('sell'), categoryData.category, spaceText), link: `/${t('sell')}/${categoryData.category}` },
      { text: formatText(t('rent'), categoryData.category, spaceText), link: `/${t('rent')}/${categoryData.category}` },
      { text: formatText(categoryData.category, 'New Project', spaceText), link: `/${t('project')}/${categoryData.category}` },
      { text: formatText(categoryData.category, 'near Mass Transit', spaceText), link: `/${t('sell')}/${categoryData.category}/${t('Mass Transit')}` },
      { text: formatText(categoryData.category, 'Lumpini', spaceText), link: `/${t('sell')}/${categoryData.category}/${t('Public Park')}/${t('Lumpini')}` },
      { text: formatText(categoryData.category, 'Sukhumvit', spaceText), link: `/${t('sell')}/${categoryData.category}/${t('Road')}/${t('Sukhumvit')}` },
      { text: formatText(categoryData.category, 'Ratchada', spaceText), link: `/${t('sell')}/${categoryData.category}/${t('Road')}/${t('Ratchada')}` },
      { text: formatText(categoryData.category, 'Lat Phrao', spaceText), link: `/${t('sell')}/${categoryData.category}/${t('Bangkok')}/${t('Lat Phrao')}` },
      { text: formatText(categoryData.category, 'Pattaya', spaceText), link: `/${t('sell')}/${categoryData.category}/${t('Beach')}/${t('Pattaya')}` },
      { text: formatText(categoryData.category, 'Chiang Mai', spaceText), link: `/${t('sell')}/${categoryData.category}/${t('Chiang Mai')}` },
      { text: formatText(categoryData.category, 'Hua Hin', spaceText), link: `/${t('sell')}/${categoryData.category}/${t('Prachuap Khiri Khan')}/${t('Hua Hin')}` }
    ];

    // Remove project link for land and apartment
    if([t('land'), t('apartment')].includes(categoryData.category)) {
      list.splice(2, 1);
    }

    return list;
  };

  // Additional menu items
  const organizationLinks = [
    { text: t('footer.about_us'), link: '/about' },
    { text: t('footer.privacy_policy'), link: '/privacy-policy' },
    { text: t('footer.terms_conditions'), link: '/terms' },
    { text: t('footer.post_for_free'), link: '/post' },
    { text: t('footer.contact_us'), link: '/contact' }
  ];

  const sitemapLinks = [
    { text: 'Sitemap TH', link: '/web-sitemap/th' },
    { text: 'Sitemap EN', link: '/web-sitemap/en' },
    { text: 'Sitemap CN', link: '/web-sitemap/cn' }
  ];

  return (
    <footer style={{
      backgroundColor: '#F5F5F6',
      padding: '24px 0',
      fontFamily: 'Prompt, Inter, Noto Serif SC, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Oxygen, Ubuntu, Cantarell, Fira Sans, Droid Sans, Helvetica Neue, sans-serif'
    }}>
      <div style={{
        padding: '0 16px'
      }}>
        {/* Accordion Section */}
        <div style={{ marginBottom: '24px' }}>
          {/* Category Accordions */}
          {categoryData.map((category, index) => {
            const categoryTitle = locale === 'cn' ? 
              `${t('popular', { ns: 'common' })}${category.category}`: 
              `${category.category}${t('popular', { ns: 'common' })}`;

            const links = generateLinks(category);
            const isExpanded = accordionState[category.key] || false;

            return (
              <div key={index} style={{ marginBottom: '4px' }}>
                {/* Accordion Header */}
                <div 
                  onClick={() => toggleAccordion(category.key)}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    padding: '8px 0',
                    cursor: 'pointer',
                    background: 'transparent'
                  }}
                >
                  <h5 style={{
                    fontFamily: 'Prompt',
                    fontWeight: 500,
                    fontSize: '14px',
                    lineHeight: '1.25',
                    color: '#000000',
                    margin: 0
                  }}>
                    {categoryTitle}
                  </h5>
                  
                  {/* Arrow Icon */}
                  <svg 
                    width="12" 
                    height="7" 
                    viewBox="0 0 12 7" 
                    fill="none" 
                    style={{
                      transform: isExpanded ? 'rotate(180deg)' : 'rotate(0deg)',
                      transition: 'transform 0.3s ease',
                      color: isExpanded ? '#F4762A' : '#919192'
                    }}
                  >
                    <path 
                      d="M1 1L6 6L11 1" 
                      stroke="currentColor" 
                      strokeWidth="1.5" 
                      strokeLinecap="round" 
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>

                {/* Accordion Content */}
                {isExpanded && (
                  <div style={{
                    padding: '12px 0',
                    animation: 'fadeIn 0.3s ease-in-out'
                  }}>
                    <div style={{ 
                      display: 'flex', 
                      flexDirection: 'column', 
                      gap: '8px' 
                    }}>
                      {links.map((item, linkIndex) => (
                        <a key={linkIndex} href={item.link} style={{
                          fontFamily: 'Prompt',
                          fontWeight: 400,
                          fontSize: '12px',
                          lineHeight: '1.6',
                          color: '#575758',
                          textDecoration: 'none',
                          transition: 'color 0.2s ease',
                          padding: '2px 0'
                        }}>
                          {item.text}
                        </a>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {/* Organization Accordion */}
          <div style={{ marginBottom: '4px' }}>
            <div 
              onClick={() => toggleAccordion('organization')}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                cursor: 'pointer',
                background: 'transparent'
              }}
            >
              <h5 style={{
                fontFamily: 'Prompt',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '1.25',
                color: '#000000',
                margin: 0
              }}>
                                  {t('footer.organization')}
              </h5>
              
              <svg 
                width="12" 
                height="7" 
                viewBox="0 0 12 7" 
                fill="none" 
                style={{
                  transform: accordionState['organization'] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: accordionState['organization'] ? '#F4762A' : '#919192'
                }}
              >
                <path 
                  d="M1 1L6 6L11 1" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {accordionState['organization'] && (
              <div style={{
                padding: '12px 0',
                animation: 'fadeIn 0.3s ease-in-out'
              }}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '8px' 
                }}>
                  {organizationLinks.map((item, linkIndex) => (
                    <a key={linkIndex} href={item.link} style={{
                      fontFamily: 'Prompt',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '1.6',
                      color: '#575758',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease',
                      padding: '2px 0'
                    }}>
                      {item.text}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Sitemap Accordion */}
          <div style={{ marginBottom: '4px' }}>
            <div 
              onClick={() => toggleAccordion('sitemap')}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '8px 0',
                cursor: 'pointer',
                background: 'transparent'
              }}
            >
              <h5 style={{
                fontFamily: 'Prompt',
                fontWeight: 500,
                fontSize: '14px',
                lineHeight: '1.25',
                color: '#000000',
                margin: 0
              }}>
                                  {t('footer.sitemap')}
              </h5>
              
              <svg 
                width="12" 
                height="7" 
                viewBox="0 0 12 7" 
                fill="none" 
                style={{
                  transform: accordionState['sitemap'] ? 'rotate(180deg)' : 'rotate(0deg)',
                  transition: 'transform 0.3s ease',
                  color: accordionState['sitemap'] ? '#F4762A' : '#919192'
                }}
              >
                <path 
                  d="M1 1L6 6L11 1" 
                  stroke="currentColor" 
                  strokeWidth="1.5" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>

            {accordionState['sitemap'] && (
              <div style={{
                padding: '12px 0',
                animation: 'fadeIn 0.3s ease-in-out'
              }}>
                <div style={{ 
                  display: 'flex', 
                  flexDirection: 'column', 
                  gap: '8px' 
                }}>
                  {sitemapLinks.map((item, linkIndex) => (
                    <a key={linkIndex} href={item.link} style={{
                      fontFamily: 'Prompt',
                      fontWeight: 400,
                      fontSize: '12px',
                      lineHeight: '1.6',
                      color: '#575758',
                      textDecoration: 'none',
                      transition: 'color 0.2s ease',
                      padding: '2px 0'
                    }}>
                      {item.text}
                    </a>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Divider Line */}
        <div style={{
          width: '100%',
          height: '1px',
          backgroundColor: '#E1E1E2',
          margin: '24px 0'
        }} />

        {/* Company Info Section - Single Column like Desktop */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '0px',
          marginBottom: '24px'
        }}>
          {/* Logo */}
          <div style={{
            width: '98px',
            height: '118px',
            position: 'relative'
          }}>
            <Image
              src="/assets/new-logo.png"
              alt="9asset Logo"
              fill
              sizes="(max-width: 768px) 98px, 98px"
              style={{ objectFit: 'contain' }}
              priority
            />
          </div>

          {/* Company Details */}
          <div style={{ marginTop: '30px' }}>
            <h6 style={{
              fontFamily: 'Prompt',
              fontWeight: 500,
              fontSize: '19px',
              lineHeight: '1.3',
              color: '#F4762A',
              margin: '0 0 8px 0'
            }}>
              {t('footer.company_name')}
            </h6>
            <p style={{
              fontFamily: 'Prompt',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '1.6',
              color: '#575758',
              margin: 0
            }}>
              {t('footer.company_address').split('\n').map((line, index) => (
                <React.Fragment key={index}>
                  {line}
                  {index < t('footer.company_address').split('\n').length - 1 && <br />}
                </React.Fragment>
              ))}
            </p>
          </div>

          {/* Contact Info */}
          <div style={{ 
            display: 'flex', 
            flexDirection: 'column', 
            gap: '4px',
            marginTop: '30px'
          }}>
            {/* Phone */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
              <span style={{
                fontFamily: 'Prompt',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '1.6',
                color: '#575758'
              }}>
                {t('footer.phone_label')}
              </span>
              <a href="tel:+66817131812" style={{
                fontFamily: 'Prompt',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '1.6',
                color: '#575758',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
              onTouchStart={(e) => (e.target as HTMLAnchorElement).style.color = '#F4762A'}
              onTouchEnd={(e) => (e.target as HTMLAnchorElement).style.color = '#575758'}>
                {t('footer.phone_number')}
              </a>
            </div>

            {/* Email */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <span style={{
                fontFamily: 'Prompt',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '1.6',
                color: '#575758'
              }}>
                {t('footer.email_label')}
              </span>
              <a href="mailto:9asset.com@gmail.com" style={{
                fontFamily: 'Prompt',
                fontWeight: 400,
                fontSize: '16px',
                lineHeight: '1.6',
                color: '#575758',
                textDecoration: 'none',
                transition: 'color 0.2s ease'
              }}
              onTouchStart={(e) => (e.target as HTMLAnchorElement).style.color = '#F4762A'}
              onTouchEnd={(e) => (e.target as HTMLAnchorElement).style.color = '#575758'}>
                {t('footer.email_address')}
              </a>
            </div>

            {/* Social Media */}
            <div style={{ 
              display: 'flex', 
              alignItems: 'center', 
              gap: '16px',
              marginTop: '8px'
            }}>
            {/* LinkedIn */}
            <a 
              href="https://linkedin.com/company/9asset" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#F4762A',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(244, 118, 42, 0.3)',
                textDecoration: 'none'
              }}
              onTouchStart={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1.1)';
                target.style.backgroundColor = '#e85a15';
                target.style.boxShadow = '0 4px 12px rgba(244, 118, 42, 0.5)';
              }}
              onTouchEnd={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1)';
                target.style.backgroundColor = '#F4762A';
                target.style.boxShadow = '0 2px 8px rgba(244, 118, 42, 0.3)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" fill="white"/>
              </svg>
            </a>

            {/* Facebook */}
            <a 
              href="https://facebook.com/9asset" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#F4762A',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(244, 118, 42, 0.3)',
                textDecoration: 'none'
              }}
              onTouchStart={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1.1)';
                target.style.backgroundColor = '#e85a15';
                target.style.boxShadow = '0 4px 12px rgba(244, 118, 42, 0.5)';
              }}
              onTouchEnd={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1)';
                target.style.backgroundColor = '#F4762A';
                target.style.boxShadow = '0 2px 8px rgba(244, 118, 42, 0.3)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" fill="white"/>
              </svg>
            </a>

            {/* YouTube */}
            <a 
              href="https://youtube.com/@9asset" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#F4762A',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(244, 118, 42, 0.3)',
                textDecoration: 'none'
              }}
              onTouchStart={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1.1)';
                target.style.backgroundColor = '#e85a15';
                target.style.boxShadow = '0 4px 12px rgba(244, 118, 42, 0.5)';
              }}
              onTouchEnd={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1)';
                target.style.backgroundColor = '#F4762A';
                target.style.boxShadow = '0 2px 8px rgba(244, 118, 42, 0.3)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" fill="white"/>
              </svg>
            </a>

            {/* Instagram */}
            <a 
              href="https://instagram.com/9asset" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#F4762A',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(244, 118, 42, 0.3)',
                textDecoration: 'none'
              }}
              onTouchStart={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1.1)';
                target.style.backgroundColor = '#e85a15';
                target.style.boxShadow = '0 4px 12px rgba(244, 118, 42, 0.5)';
              }}
              onTouchEnd={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1)';
                target.style.backgroundColor = '#F4762A';
                target.style.boxShadow = '0 2px 8px rgba(244, 118, 42, 0.3)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z" fill="white"/>
              </svg>
            </a>

            {/* TikTok */}
            <a 
              href="https://tiktok.com/@9asset" 
              target="_blank" 
              rel="noopener noreferrer"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '50%',
                backgroundColor: '#F4762A',
                transition: 'all 0.3s ease',
                boxShadow: '0 2px 8px rgba(244, 118, 42, 0.3)',
                textDecoration: 'none'
              }}
              onTouchStart={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1.1)';
                target.style.backgroundColor = '#e85a15';
                target.style.boxShadow = '0 4px 12px rgba(244, 118, 42, 0.5)';
              }}
              onTouchEnd={(e) => {
                const target = e.currentTarget as HTMLAnchorElement;
                target.style.transform = 'scale(1)';
                target.style.backgroundColor = '#F4762A';
                target.style.boxShadow = '0 2px 8px rgba(244, 118, 42, 0.3)';
              }}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
                <path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z" fill="white"/>
              </svg>
            </a>
            </div>
          </div>
        </div>

        {/* Bottom Section - Copyright and Back to Top */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
          paddingTop: '28px',
          paddingBottom: '60px'
        }}>
          <p style={{
            fontFamily: 'Prompt',
            fontWeight: 400,
            fontSize: '14px',
            lineHeight: '1.6',
            color: '#575758',
            margin: 0
          }}>
            {t('footer.copyright')} v{process.env.NEXT_PUBLIC_BUILD_NUMBER || '3.0.0'}
          </p>

          <button 
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              fontFamily: 'Prompt',
              fontWeight: 400,
              fontSize: '16px',
              lineHeight: '1.6',
              color: '#000000',
              transition: 'color 0.2s ease',
              width: '132px',
              height: '28px'
            }}
          >
            Back to Top
            <svg width="17" height="17" viewBox="0 0 17 18" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path fillRule="evenodd" clipRule="evenodd" d="M8.5 1.5C4.35786 1.5 1 4.85786 1 9C1 13.1421 4.35786 16.5 8.5 16.5C12.6421 16.5 16 13.1421 16 9C16 4.85786 12.6421 1.5 8.5 1.5ZM0 9C0 4.30558 3.80558 0.5 8.5 0.5C13.1944 0.5 17 4.30558 17 9C17 13.6944 13.1944 17.5 8.5 17.5C3.80558 17.5 0 13.6944 0 9Z" fill="#F4762A"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M8.14645 6.14645C8.34171 5.95118 8.65829 5.95118 8.85355 6.14645L12.3536 9.64645C12.5488 9.84171 12.5488 10.1583 12.3536 10.3536C12.1583 10.5488 11.8417 10.5488 11.6464 10.3536L8.5 7.20711L5.35355 10.3536C5.15829 10.5488 4.84171 10.5488 4.64645 10.3536C4.45118 10.1583 4.45118 9.84171 4.64645 9.64645L8.14645 6.14645Z" fill="#F4762A"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Add animation styles */}
      <style jsx>{`
        @keyframes fadeIn {
          0% {
            opacity: 0;
            transform: translateY(-10px);
          }
          100% {
            opacity: 1;
            transform: translateY(0);
          }
        }
      `}</style>
    </footer>
  );
};

export default NewFooterMobile;
